import HyperDX from '@hyperdx/browser'
import { getUrlParams } from '@/utils/common'

// 与 request.js 拦截器口径一致：仅 5xx / 网络错误 / 协议异常上报，4xx 业务错误不上报避免噪音。
function reportThemeFailure(error: unknown, attrs: Record<string, unknown> = {}): void {
  try {
    const err = error instanceof Error ? error : new Error(String((error as any)?.message ?? error))
    HyperDX.recordException(err, { 'dvdfab.error.source': 'theme.fetch', ...attrs })
  } catch (_e) {
    // HyperDX 未初始化时静默
  }
}

// Preprocess tokens (handle keys with '/')
function preprocessTokens(data: any): any {
  const newData: any = {}
  Object.keys(data).forEach(key => {
    if (key.includes('/')) {
      const children = data[key]
      Object.assign(newData, children)
    } else {
      newData[key] = data[key]
    }
  })
  return newData
}

const isObject = (val: any) => val && typeof val === 'object' && !Array.isArray(val)

function resolveReference(value: string, map: Record<string, any>): string | number {
  if (typeof value !== 'string') return value
  const regex = /\{([^}]+)\}/g
  let resolved = value
  let match
  let depth = 0

  // Replace all references
  while ((match = regex.exec(resolved)) !== null && depth < 10) {
    const refPath = match[1]
    let refValue = map[refPath]

    // If refValue is also a token object (has $value), extract it
    if (isObject(refValue) && (refValue.$value || refValue.value)) {
      refValue = refValue.$value || refValue.value
    }

    if (refValue !== undefined && (typeof refValue === 'string' || typeof refValue === 'number')) {
      resolved = resolved.replace(match[0], String(refValue))
      regex.lastIndex = 0 // Restart regex
    }
    depth++
  }
  return resolved
}

function generateCSS(tokens: any): string {
  const flattened: Record<string, any> = {}
  const referenceMap: Record<string, any> = {}

  // 1. Build Reference Map (dot notation)
  function buildMap(obj: any, path: string[]) {
    for (const key in obj) {
      if (key.startsWith('$')) continue
      const value = obj[key]
      const currentPath = [...path, key]

      if (isObject(value)) {
        // Store in map
        referenceMap[currentPath.join('.')] = value
        // If it's a leaf token (has $value), it's also a value
        if (value.$value || value.value) {
          // It is stored as object, resolveReference will handle extracting value
        }
        buildMap(value, currentPath)
      }
    }
  }
  buildMap(tokens, [])

  // 2. Flatten for CSS (kebab-case)
  function flatten(obj: any, path: string[]) {
    for (const key in obj) {
      if (key.startsWith('$')) continue
      const value = obj[key]

      if (isObject(value) && (value.$value || value.value)) {
        const tokenKey = [...path, key].join('-')
        flattened[tokenKey] = value.$value || value.value

        // Handle typography composite
        if (value.$type === 'typography' || value.type === 'typography') {
          const val = value.$value || value.value
          if (isObject(val) && val.fontWeight) {
            // Resolve inside
            const w = resolveReference(val.fontWeight, referenceMap)
            const s = resolveReference(val.fontSize, referenceMap)
            const h = resolveReference(val.lineHeight, referenceMap)
            const f = resolveReference(val.fontFamily, referenceMap)
            flattened[tokenKey] = `${w} ${s}/${h} ${f}`
          }
        }
      } else if (isObject(value)) {
        flatten(value, [...path, key])
      }
    }
  }
  flatten(tokens, [])

  // 3. Generate CSS
  let css = ':root {\n'
  for (const [key, val] of Object.entries(flattened)) {
    const resolvedVal = resolveReference(val as string, referenceMap)
    css += `  --${key}: ${resolvedVal};\n`
  }
  css += '}\n'
  return css
}

export async function fetchAndProcessTheme(
  apiUrl: string,
  defaultGitUrl?: string
): Promise<string> {
  const query = getUrlParams(window.location.href)
  let layoutCss = ''
  let apiThemeCss = ''

  try {
    // Call API
    const apiResponse = await fetch(apiUrl, {
      headers: {
        'domain-name': query['domain'] || '',
        Accept: 'application/json'
      },
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      // 确保使用正确的协议
      cache: 'no-cache'
    })
    // 协议异常 / 5xx：交给 HyperDX 统一上报，与 axios 拦截器口径对齐
    if (apiResponse.status === 426 || apiResponse.status >= 500) {
      reportThemeFailure(new Error(`Theme API HTTP ${apiResponse.status}`), {
        'http.status_code': apiResponse.status,
        'http.url': apiUrl
      })
      return ''
    }

    if (apiResponse.ok) {
      const res = await apiResponse.json()
      // console.log('Theme API Result:', res); // Removed diagnostic log
      const dataObj = res.data || res // Handle both structures

      const themeCss = dataObj.theme_css || ''
      if (typeof themeCss === 'string' && themeCss.trim()) {
        let filteredCss = themeCss

        // Use regex to extract layout variables more robustly and remove them from raw CSS
        const extractAndFilter = (key: string) => {
          const regex = new RegExp(`\\.?${key}\\s*:\\s*([^;\\n]+);?`, 'g')
          const match = themeCss.match(regex)
          if (match) {
            // Get value from the first match
            const valMatch = match[0].match(new RegExp(`\\.?${key}\\s*:\\s*([^;\\n]+)`))
            const val = valMatch ? valMatch[1].trim() : null
            // Filter out these pseudo-rules from the actual CSS string to avoid browser syntax errors
            filteredCss = filteredCss.replace(regex, '')
            return val
          }
          return null
        }

        const paddingX = extractAndFilter('bg_page_padding_x')
        const paddingTop = extractAndFilter('bg_page_header_padding_top')
        const bgColor = extractAndFilter('bg_page_bg_color')

        apiThemeCss = filteredCss

        if (paddingX) {
          layoutCss += `  --bg-page-padding-x: ${paddingX};\n`
          document.documentElement.style.setProperty('--bg-page-padding-x', paddingX)
        }
        if (paddingTop) {
          layoutCss += `  --bg-page-header-padding-top: ${paddingTop};\n`
          document.documentElement.style.setProperty('--bg-page-header-padding-top', paddingTop)
        }
        if (bgColor) {
          layoutCss += `  --bg-page-bg-color: ${bgColor};\n`
          document.documentElement.style.setProperty('--bg-page-bg-color', bgColor)
        }

        // 如果 theme_css 不为空，返回处理后的 CSS
        if (apiThemeCss.trim()) {
          return layoutCss ? `:root {\n${layoutCss}}\n${apiThemeCss}` : apiThemeCss
        }
      }
    }
  } catch (e: any) {
    // 网络异常 / DNS / CORS / 协议异常等：fetch reject 走这里，统一上报 HyperDX
    reportThemeFailure(e, { 'http.url': apiUrl })
  }

  // 如果 theme_css 为空或接口调用失败，返回空字符串（使用系统样式）
  return ''
}
